<template>
  <div class="orchard-editor">
    <div class="orchard-grid">

      <div class="form-group" style="grid-column: 1 / -1;">
        <label>{{ t('plotOrchardEditor.variety') }}</label>
        <VarietyChooser v-model="local.cultivar" :placeholder="t('plotOrchardEditor.varietyPlaceholder')" />
      </div>

      <div class="form-group">
        <label>{{ t('plotOrchardEditor.plantingYear') }}</label>
        <input type="number" v-model.number="local.planting_year"
          :placeholder="t('plotOrchardEditor.plantingYearPlaceholder')" min="1900" :max="new Date().getFullYear()" />
      </div>

      <div class="form-group">
        <label>{{ t('plotOrchardEditor.vigourClass') }}</label>
        <select v-model="local.vigour">
          <option value="">{{ t('plotOrchardEditor.notSpecifiedF') }}</option>
          <option value="low">{{ t('plotOrchardEditor.vigourLow') }}</option>
          <option value="medium">{{ t('plotOrchardEditor.vigourMedium') }}</option>
          <option value="high">{{ t('plotOrchardEditor.vigourHigh') }}</option>
        </select>
      </div>

      <div class="form-group">
        <label>{{ t('plotOrchardEditor.naturalGrassing') }}</label>
        <select v-model="local.inerbimento_naturale">
          <option value="">{{ t('plotOrchardEditor.notSpecifiedM') }}</option>
          <option value="si">{{ t('plotOrchardEditor.yes') }}</option>
          <option value="no">{{ t('plotOrchardEditor.no') }}</option>
        </select>
      </div>

      <div class="form-group">
        <label>{{ t('plotOrchardEditor.rowDistance') }}</label>
        <input type="number" v-model.number="local.dist_x"
          :placeholder="t('plotOrchardEditor.rowDistancePlaceholder')" min="0" step="0.1" @input="calcDensity" />
      </div>

      <div class="form-group">
        <label>{{ t('plotOrchardEditor.plantDistance') }}</label>
        <input type="number" v-model.number="local.dist_y"
          :placeholder="t('plotOrchardEditor.plantDistancePlaceholder')" min="0" step="0.1" @input="calcDensity" />
      </div>

    </div>

    <!-- Densità calcolata -->
    <div v-if="local.dist_x && local.dist_y" class="density-box">
      <AeIcon name="Trees" :size="16" />
      <span class="density-label">{{ t('plotOrchardEditor.plantingDensity') }}</span>
      <span class="density-value">{{ t('plotOrchardEditor.plantsPerHa', { density: local.density }) }}</span>
      <span class="density-formula">= 10.000 / ({{ local.dist_x }} × {{ local.dist_y }})</span>
    </div>

  </div>
</template>

<script setup>
import { ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { AeIcon } from '@aevue/components'
import VarietyChooser from './VarietyChooser.vue'

const { t } = useI18n()

const props = defineProps({
  modelValue: { type: Object, default: () => ({}) }
})
const emit = defineEmits(['update:modelValue'])

const EMPTY = () => ({
  cultivar: [],
  planting_year: null,
  vigour: '',
  inerbimento_naturale: '',
  dist_x: null,
  dist_y: null,
  density: null,
})

const local = ref({ ...EMPTY(), ...props.modelValue })

watch(() => props.modelValue, val => {
  if (JSON.stringify(val) !== JSON.stringify(local.value)) {
    local.value = { ...EMPTY(), ...val }
  }
}, { deep: true })

watch(local, val => {
  emit('update:modelValue', { ...val })
}, { deep: true })

function calcDensity() {
  const x = parseFloat(local.value.dist_x)
  const y = parseFloat(local.value.dist_y)
  local.value.density = (x > 0 && y > 0) ? Math.round(10000 / (x * y)) : null
}
</script>

<style scoped>
.orchard-editor {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.orchard-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.6rem 1rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.form-group label {
  font-size: 0.8rem;
  font-weight: 500;
  color: var(--text-secondary, #666);
}

.form-group input,
.form-group select {
  padding: 0.45rem 0.6rem;
  border: 1px solid var(--border-color, #e2e8f0);
  border-radius: var(--border-radius, 8px);
  font-family: var(--font-family);
  font-size: 0.875rem;
  background: rgba(255, 255, 255, 0.5);
  color: var(--text-color);
  transition: border-color 0.15s;
}

.form-group input:focus,
.form-group select:focus {
  outline: none;
  border-color: var(--primary-color, #4299e1);
}

.density-box {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 0.9rem;
  background: rgba(66, 153, 225, 0.07);
  border: 1px solid rgba(66, 153, 225, 0.2);
  border-radius: var(--border-radius, 8px);
  font-size: 0.85rem;
  flex-wrap: wrap;
}

.density-label {
  color: var(--text-secondary, #666);
}

.density-value {
  font-weight: 700;
  color: var(--primary-color, #2b6cb0);
}

.density-formula {
  color: var(--text-secondary, #999);
  font-size: 0.75rem;
  margin-left: auto;
}

@media (max-width: 480px) {
  .orchard-grid { grid-template-columns: 1fr; }
  .density-formula { display: none; }
}
</style>
